import {
  LocationAlarmCamera,
  UnifiedMonitoringProduct,
} from '@hakimo-ui/hakimo/types';
import { trackEvent } from '@hakimo-ui/hakimo/util';
import { Alert } from '@hakimo-ui/shared/ui-base';
import { useState } from 'react';
import { TalkdownStep } from './sop-card-utils';
import { CollapsibleCard } from './CollapsibleCard';
import { TalkdownCameraPicker } from './TalkdownCameraPicker';
import { TalkdownComposer } from './TalkdownComposer';
import { useSendTalkdown } from './useSendTalkdown';

interface LiveTalkdownCardProps {
  cameras: LocationAlarmCamera[];
  locationAlarmId: string;
  escalationId?: string;
  product?: UnifiedMonitoringProduct;
  disabled?: boolean;
  steps: TalkdownStep[];
  stepsDone: number;
  onSent: () => void;
}

export function LiveTalkdownCard(props: LiveTalkdownCardProps) {
  const {
    cameras,
    locationAlarmId,
    escalationId,
    product,
    disabled,
    steps,
    stepsDone,
    onSent,
  } = props;

  const activeStep: TalkdownStep | undefined = steps[stepsDone];

  const [selectedCameras, setSelectedCameras] = useState<LocationAlarmCamera[]>(
    () => {
      const first = cameras.find((cam) => cam.isTalkdownEnabled);
      return first ? [first] : [];
    }
  );

  const trackingMeta = {
    source: 'sop_live_talkdown',
    cameraName: selectedCameras.map((cam) => cam.name).join(', '),
  };
  const mutation = useSendTalkdown({
    locationAlarmId,
    escalationId,
    product,
    onSuccess: (cameraIds) => {
      cameraIds.forEach((cameraId) =>
        trackEvent('submit_camera_details_audio_talkdown', {
          camera_id: cameraId,
          locationAlarmId,
        })
      );
      onSent();
    },
  });

  const onSend = (file: File) => {
    if (selectedCameras.length === 0) {
      return;
    }
    const formData = new FormData();
    formData.append('audio', file);
    mutation.mutate({
      cameraIds: selectedCameras.map((cam) => cam.id),
      audio: formData,
    });
  };

  return (
    <CollapsibleCard
      variant="surface"
      title="Live Talkdown"
      collapsible={false}
      ruled
      headerAction={
        <TalkdownCameraPicker
          cameras={cameras}
          value={selectedCameras}
          onChange={setSelectedCameras}
        />
      }
      below={
        steps.length > 0 && (
          <div className="pt-3">
            <Alert
              type={stepsDone >= steps.length ? 'success' : 'error'}
              size="small"
            >
              {`${stepsDone} of ${steps.length} Talkdowns conducted`}
            </Alert>
          </div>
        )
      }
    >
      <TalkdownComposer
        key={activeStep?.index ?? 'no-step'}
        step={activeStep}
        onSend={onSend}
        isSending={mutation.isLoading}
        disabled={disabled || selectedCameras.length === 0}
        trackingMeta={trackingMeta}
      />
    </CollapsibleCard>
  );
}

export default LiveTalkdownCard;
